/**
 * Section: Reset Within Reach – Gallery & Thumbnail Slider
 * Handles main image slides, thumbnail synchronization, autoplay, arrows, and responsive touch gestures.
 */
(function() {
  'use strict';

  function parseBool(val) {
    return val === 'true' || val === true || val === '1' || val === 1;
  }

  class ResetWithinReachGallery {
    constructor(element) {
      this.container = element;
      this.thumbsWrapper = this.container.querySelector('.js-thumbs-wrapper');
      this.thumbsTrack = this.container.querySelector('.js-thumbs-track');
      this.thumbs = Array.from(this.container.querySelectorAll('.js-thumb'));
      this.thumbsPrev = this.container.querySelector('.js-thumbs-prev');
      this.thumbsNext = this.container.querySelector('.js-thumbs-next');

      this.sliderWrapper = this.container.querySelector('.js-main-slider-wrapper');
      this.mainSlider = this.container.querySelector('.js-main-slider');
      this.slides = Array.from(this.container.querySelectorAll('.js-slide'));
      this.sliderPrev = this.container.querySelector('.js-slider-prev');
      this.sliderNext = this.container.querySelector('.js-slider-next');

      this.currentIndex = 0;
      this.totalSlides = this.slides.length;
      this.autoplayTimer = null;

      // Dataset config
      const data = this.container.dataset || {};
      this.autoplayEnabled = parseBool(data.autoplay);
      this.autoplayInterval = parseInt(data.autoplayInterval, 10) || 4000;

      // Touch handling
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchEndX = 0;
      this.touchEndY = 0;

      if (this.totalSlides <= 1) {
        if (this.thumbsWrapper) this.thumbsWrapper.style.display = 'none';
        if (this.sliderPrev) this.sliderPrev.style.display = 'none';
        if (this.sliderNext) this.sliderNext.style.display = 'none';
        return;
      }

      this.init();
    }

    init() {
      this.bindThumbnails();
      this.bindSliderArrows();
      this.bindThumbArrows();
      this.bindTouch();
      this.bindHoverPause();

      if (this.autoplayEnabled) {
        this.startAutoplay();
      }

      // IntersectionObserver to pause autoplay when off-screen
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (this.autoplayEnabled) this.startAutoplay();
            } else {
              this.stopAutoplay();
            }
          });
        }, { threshold: 0.2 });
        this.observer.observe(this.container);
      }
    }

    bindThumbnails() {
      this.thumbs.forEach((thumb, idx) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this.goTo(idx);
        });

        thumb.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.goTo(idx);
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            const nextIdx = (idx + 1) % this.totalSlides;
            this.thumbs[nextIdx].focus();
            this.goTo(nextIdx);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            const prevIdx = (idx - 1 + this.totalSlides) % this.totalSlides;
            this.thumbs[prevIdx].focus();
            this.goTo(prevIdx);
          }
        });
      });
    }

    bindSliderArrows() {
      if (this.sliderPrev) {
        this.sliderPrev.addEventListener('click', (e) => {
          e.preventDefault();
          this.prev();
        });
      }

      if (this.sliderNext) {
        this.sliderNext.addEventListener('click', (e) => {
          e.preventDefault();
          this.next();
        });
      }
    }

    bindThumbArrows() {
      if (this.thumbsPrev) {
        this.thumbsPrev.addEventListener('click', (e) => {
          e.preventDefault();
          this.prev();
        });
      }

      if (this.thumbsNext) {
        this.thumbsNext.addEventListener('click', (e) => {
          e.preventDefault();
          this.next();
        });
      }
    }

    bindTouch() {
      if (!this.mainSlider) return;

      this.mainSlider.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
        this.touchStartY = e.changedTouches[0].screenY;
      }, { passive: true });

      this.mainSlider.addEventListener('touchend', (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this.touchEndY = e.changedTouches[0].screenY;
        this.handleGesture();
      }, { passive: true });
    }

    handleGesture() {
      const deltaX = this.touchEndX - this.touchStartX;
      const deltaY = this.touchEndY - this.touchStartY;
      const threshold = 40;

      // Verify horizontal swipe dominates vertical scroll
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > threshold) {
        if (deltaX < 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    }

    bindHoverPause() {
      if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) {
        return;
      }

      this.container.addEventListener('mouseenter', () => {
        this.stopAutoplay();
      });

      this.container.addEventListener('mouseleave', () => {
        if (this.autoplayEnabled) {
          this.startAutoplay();
        }
      });
    }

    goTo(index) {
      if (index === this.currentIndex || index < 0 || index >= this.totalSlides) return;

      // Update Slides
      this.slides.forEach((slide, i) => {
        if (i === index) {
          slide.classList.add('is-active');
          slide.setAttribute('aria-hidden', 'false');
        } else {
          slide.classList.remove('is-active');
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      // Update Thumbnails
      this.thumbs.forEach((thumb, i) => {
        if (i === index) {
          thumb.classList.add('is-active');
          thumb.setAttribute('aria-selected', 'true');
          thumb.setAttribute('tabindex', '0');
        } else {
          thumb.classList.remove('is-active');
          thumb.setAttribute('aria-selected', 'false');
          thumb.setAttribute('tabindex', '-1');
        }
      });

      this.currentIndex = index;
      this.scrollThumbToActive();

      // Reset Autoplay timer on manual interaction
      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    }

    scrollThumbToActive() {
      const activeThumb = this.thumbs[this.currentIndex];
      if (!activeThumb || !this.thumbsTrack) return;

      const trackWidth = this.thumbsTrack.clientWidth;
      const thumbLeft = activeThumb.offsetLeft;
      const thumbWidth = activeThumb.offsetWidth;
      const targetScroll = thumbLeft - (trackWidth / 2) + (thumbWidth / 2);

      this.thumbsTrack.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: 'smooth'
      });
    }

    next() {
      const nextIdx = (this.currentIndex + 1) % this.totalSlides;
      this.goTo(nextIdx);
    }

    prev() {
      const prevIdx = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
      this.goTo(prevIdx);
    }

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this.next();
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    destroy() {
      this.stopAutoplay();
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  function initSection(scope) {
    const root = scope || document;
    const galleries = root.querySelectorAll('.js-reset-reach-gallery');
    galleries.forEach(el => {
      if (!el._resetReachGallery) {
        el._resetReachGallery = new ResetWithinReachGallery(el);
      }
    });
  }

  // Initialize on DOM load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSection());
  } else {
    initSection();
  }

  // Shopify Theme Editor Support
  document.addEventListener('shopify:section:load', (e) => {
    initSection(e.target);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const gallery = e.target.querySelector('.js-reset-reach-gallery');
    if (gallery && gallery._resetReachGallery) {
      gallery._resetReachGallery.destroy();
      delete gallery._resetReachGallery;
    }
  });

  document.addEventListener('shopify:block:select', (e) => {
    const blockEl = e.target;
    const blockId = blockEl.getAttribute('data-block-id') || e.detail.blockId;
    const galleryEl = blockEl.closest('.js-reset-reach-gallery') || document.querySelector('.js-reset-reach-gallery');
    if (galleryEl && galleryEl._resetReachGallery) {
      const inst = galleryEl._resetReachGallery;
      const targetIndex = inst.thumbs.findIndex(t => t.dataset.blockId === blockId || t.getAttribute('id')?.includes(blockId));
      if (targetIndex !== -1) {
        inst.goTo(targetIndex);
      }
    }
  });

})();
